/**
 * Actor home — list every project the signed-in actor is assigned to.
 *
 * SLP Professional shell (redesign V2): one SLP panel per assignment with a
 * blue title bar (CODE · EP), tool-dense body, and the two CTAs. Data flow
 * (useActorProjects, role guard, router) unchanged.
 */
import { EmptyState, SlpButton, SlpPanel, SlpStatusLamp, Spinner } from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useActorProjects } from '../../lib/api/projects'
import { useRequireRole } from '../../lib/auth'

export const Route = createFileRoute('/actor/')({
  component: ActorHome,
})

function ActorHome() {
  useRequireRole(['actor'])
  const projects = useActorProjects()

  const list = projects.data?.projects ?? []

  return (
    <section className="flex flex-col gap-3 px-1 py-2">
      <header className="flex flex-col gap-0.5">
        <h1 className="text-[16px] font-bold tracking-tight text-[var(--text)] m-0">
          My projects
        </h1>
        <p className="slp-mono m-0" style={{ fontSize: 10, color: 'var(--slp-text-dim)' }}>
          {projects.isLoading
            ? 'LOADING ASSIGNMENTS…'
            : list.length === 0
              ? 'NO ASSIGNMENTS YET'
              : `${list.length} ACTIVE PROJECT${list.length === 1 ? '' : 'S'}`}
        </p>
      </header>

      {projects.isLoading ? (
        <SlpPanel title="Assignments">
          <div className="flex items-center gap-2" style={{ color: 'var(--slp-text-dim)' }}>
            <Spinner /> Loading projects…
          </div>
        </SlpPanel>
      ) : projects.isError ? (
        <SlpPanel title="Assignments">
          <div className="slp-row">
            <SlpStatusLamp status="warn" />
            <span>
              Could not load projects:{' '}
              {projects.error instanceof Error ? projects.error.message : 'unknown error'} — check
              your connection and reload.
            </span>
          </div>
        </SlpPanel>
      ) : list.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="Once a producer adds you to a project, it will show up here. You can close this tab — we'll email you the moment you're booked."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p) => (
            <SlpPanel
              key={p.project_id}
              title={`${p.project_code} · EP${String(p.episode).padStart(2, '0')}`}
            >
              <div className="flex flex-col gap-2">
                <h2 className="m-0 text-[14px] font-semibold leading-snug text-[var(--text)]">
                  {p.title}
                </h2>
                <div className="slp-row" style={{ minHeight: 22 }}>
                  <SlpStatusLamp status={p.organization.is_active ? 'ok' : 'off'} size="sm" />
                  <span
                    className="slp-mono truncate"
                    style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}
                    title={p.organization.name}
                  >
                    {p.organization.name}
                  </span>
                  <span style={{ color: 'var(--slp-text-dim)', fontSize: 11 }}>
                    {p.client_name}
                  </span>
                </div>
                <div
                  className="flex items-center justify-between gap-2 pt-2"
                  style={{ borderTop: '1px solid var(--slp-panel-2)' }}
                >
                  <Link
                    to="/actor/projects/$projectId"
                    params={{ projectId: p.project_id }}
                    className="no-underline"
                  >
                    <SlpButton variant="secondary" style={{ height: 22, padding: '0 10px' }}>
                      Open →
                    </SlpButton>
                  </Link>
                  <Link
                    to="/actor/projects/$projectId/record"
                    params={{ projectId: p.project_id }}
                    className="no-underline"
                  >
                    <SlpButton style={{ height: 22, padding: '0 12px' }}>
                      ● Start recording
                    </SlpButton>
                  </Link>
                </div>
              </div>
            </SlpPanel>
          ))}
        </div>
      )}
    </section>
  )
}
